// The page: a tree of nodes in a Loro doc. Every edit a person, a tab or an
// AI makes is one of the methods here, so an edit is always data: a node's
// type, its props, its style and its text, never code.
// meets: together/converge, move/keeps-edits, move/no-duplicates, code/edits-are-data
import { LoroDoc, LoroMap, LoroText, LoroTree, LoroTreeNode, UndoManager, type Delta, type TreeID } from "loro-crdt";
import { checkProp, components, describeProp } from "./components";
import { library, NAME, type Entry, type Expose } from "./library";
import { allowStyle } from "./style";
import type { Md } from "./markdown";

export type { TreeID };
export type Props = Record<string, unknown>;
export type Style = Record<string, string>;
// What a new node is made from: the fixture, a paste, an insert. From the
// text form, it also carries the node's id and its marked-up text.
// From a file, it also carries how its block was written there (markdown.ts).
export type Spec = { type: string; props?: Props; style?: Style; text?: string; delta?: Delta<string>[]; id?: string; md?: Md; children?: Spec[] };
// A page as a whole, in the shape match() makes the doc take.
export type Target = { tokens: Record<string, string>; nodes: Spec[] };
// One node of a version, as the renderer reads it.
export type NodeJSON = { id: TreeID; type: string; props: Props; style: Style; text?: Delta<string>[]; md?: Md; children: NodeJSON[] };

// How each mark grows when you type at its edge, as in any document.
export const MARKS = {
  bold: { expand: "after" },
  italic: { expand: "after" },
  code: { expand: "none" },
  link: { expand: "none" },
} as const;

type Data = LoroMap<{ type: string; props: LoroMap; style: LoroMap; text: LoroText; md: Md }>;

// Roots that aren't the page: components made on it, and each tab's
// clipboard, where a cut block waits to be pasted.
export const DEFINITION = "definition";
export const CLIPBOARD = "clipboard";
export const HIDDEN = new Set([DEFINITION, CLIPBOARD]);

export class Page {
  readonly doc: LoroDoc;
  readonly tree: LoroTree;
  readonly tokens: LoroMap;
  readonly people: LoroMap; // peer id → the name its edits go by in history
  readonly file: LoroMap; // for a page that is a file: its tail, and the version on disk
  readonly undo: UndoManager;

  constructor(doc = new LoroDoc()) {
    this.doc = doc;
    doc.configTextStyle(MARKS);
    this.tree = doc.getTree("page");
    this.tokens = doc.getMap("tokens");
    this.people = doc.getMap("people");
    this.file = doc.getMap("file");
    // Loro's undo only ever reverts this peer's own operations.
    // meets: together/own-undo
    this.undo = new UndoManager(doc, { mergeInterval: 400, maxUndoSteps: 500, excludeOriginPrefixes: ["seed", "aside"] });
  }

  // A new page starts from a spec written under a peer id made from the
  // spec itself, so two tabs that start the same page at once write the
  // same operations, and merging them is a no-op instead of two pages.
  seed(spec: Spec[], tokens: Record<string, string> = {}, file?: Record<string, string>) {
    const own = this.doc.peerIdStr;
    const peer = seedPeer([...spec, { type: "tokens", props: tokens }, ...(file ? [{ type: "file", props: file }] : [])]);
    this.doc.setPeerId(peer);
    this.renamed = new Map();
    for (const s of spec) this.build(this.tree.createNode(), s);
    this.relink();
    for (const [k, v] of Object.entries(tokens)) this.tokens.set(k, v);
    for (const [k, v] of Object.entries(file ?? {})) this.file.set(k, v);
    this.doc.commit({ origin: "seed" });
    this.doc.setPeerId(BigInt(own) === peer ? randomPeer() : own);
  }

  // A file that isn't markdown is one text, `source`, and no tree: seeded
  // as above, under a peer id made from the text.
  get plain(): LoroText {
    return this.doc.getText("source");
  }
  seedText(text: string) {
    const own = this.doc.peerIdStr;
    const peer = seedPeer([{ type: "plain", text }]);
    this.doc.setPeerId(peer);
    this.plain.insert(0, text);
    this.doc.commit({ origin: "seed" });
    this.doc.setPeerId(BigInt(own) === peer ? randomPeer() : own);
  }

  // Nothing has ever been written: not the same as a page with no nodes,
  // which someone made by deleting them.
  get fresh() {
    return this.doc.oplogVersion().length() === 0;
  }

  node(id: TreeID): LoroTreeNode | undefined {
    if (!this.tree.has(id) || this.tree.isNodeDeleted(id)) return undefined;
    return this.tree.getNodeByID(id);
  }

  data(id: TreeID): Data {
    const n = this.node(id);
    if (!n) throw new Error(`no node ${id}`);
    return n.data as unknown as Data;
  }

  text(id: TreeID): LoroText | undefined {
    const t = this.node(id)?.data.get("text");
    return t instanceof LoroText ? t : undefined;
  }

  type(id: TreeID): string | undefined {
    return this.node(id)?.data.get("type") as string | undefined;
  }

  parent(id: TreeID): TreeID | undefined {
    return this.node(id)?.parent()?.id;
  }

  index(id: TreeID): number {
    return this.node(id)?.index() ?? -1;
  }

  children(parent?: TreeID): TreeID[] {
    const list = parent === undefined ? this.tree.roots() : this.node(parent)?.children() ?? [];
    return list.map((n) => n.id);
  }

  // Insert a node built from a spec; `index` past the end appends.
  insert(parent: TreeID | undefined, index: number, spec: Spec, origin = "edit"): TreeID {
    const at = Math.min(index, this.children(parent).length);
    const node = parent === undefined ? this.tree.createNode(undefined, at) : this.node(parent)!.createNode(at);
    this.build(node, spec);
    this.doc.commit({ origin });
    return node.id;
  }

  // A move is one operation on the tree, so the node keeps its id, its
  // text, and whatever anyone is typing in it. A move into the node's own
  // subtree is refused rather than made. `index` is the node's place among
  // its new siblings once it is there.
  move(id: TreeID, parent: TreeID | undefined, index: number, origin = "edit"): boolean {
    if (!this.node(id) || (parent !== undefined && !this.node(parent))) return false;
    if (parent !== undefined && !this.accepts(parent)) return false;
    const siblings = this.children(parent).filter((c) => c !== id);
    const at = Math.max(0, Math.min(index, siblings.length));
    if (this.parent(id) === parent && this.index(id) === at) return true;
    try {
      this.tree.move(id, parent, at);
    } catch {
      return false; // Loro's own check: a move that would make a cycle
    }
    this.doc.commit({ origin });
    return true;
  }

  remove(id: TreeID, origin = "edit") {
    if (!this.node(id)) return;
    this.tree.delete(id);
    this.doc.commit({ origin });
  }

  // Whether a node takes children, by its component; an unknown component
  // does, so nothing under it is stranded. A made component doesn't.
  accepts(id: TreeID): boolean {
    const type = this.type(id) ?? "";
    if (type === CLIPBOARD) return false;
    const c = components[type];
    return c ? !!c.children : !NAME.test(type);
  }

  // Styles go through the allowlist on the way in; the renderer checks
  // again on the way out, since a merge can bring anything.
  setStyle(id: TreeID, prop: string, value: string | null, origin = "edit"): boolean {
    const style = this.data(id).get("style");
    if (value === null || value === "") style.delete(prop);
    else if (!allowStyle(prop, value)) return false;
    else style.set(prop, value);
    this.doc.commit({ origin });
    return true;
  }

  // A prop is checked against its component's schema; a value that breaks
  // it is refused with what it must be. Returns the reason, or nothing.
  setProp(id: TreeID, key: string, value: unknown, origin = "edit"): string | undefined {
    const type = this.type(id);
    if (type === undefined) return "That block is gone.";
    const c = this.entry(type);
    if (!c) {
      // A component this build doesn't have (a tag the site draws, say)
      // never draws here, so its props are text, kept as written.
      // meets: file/unknown-tags
      if (value !== undefined && (typeof value !== "string" || value.length > 2000)) return `“${key}” is text of at most 2000 characters.`;
      if (!/^[a-z][A-Za-z0-9-]*$/.test(key)) return `“${key}” can't be a setting's name.`;
      if (value === undefined) this.data(id).get("props").delete(key);
      else this.data(id).get("props").set(key, value);
      this.doc.commit({ origin });
      return undefined;
    }
    const p = c.props[key];
    if (!p) return `${c.label} has no setting called “${key}”.`;
    if (value === undefined) this.data(id).get("props").delete(key);
    else if (!checkProp(p, value)) return `${c.label}'s “${key}” must be ${describeProp(p)}.`;
    else this.data(id).get("props").set(key, value as never);
    this.doc.commit({ origin });
    return undefined;
  }

  setToken(name: string, value: string | null, origin = "edit"): boolean {
    if (!/^--[a-z][a-z0-9-]*$/.test(name)) return false;
    if (value === null) this.tokens.delete(name);
    else if (!allowToken(value)) return false;
    else this.tokens.set(name, value);
    this.doc.commit({ origin });
    return true;
  }

  // Replace a node's words, as the smallest edit that does it.
  setText(id: TreeID, to: string | Delta<string>[], origin = "edit"): boolean {
    const text = this.text(id);
    if (!text) return false;
    setDelta(text, typeof to === "string" ? (to ? [{ insert: to }] : []) : to);
    this.doc.commit({ origin });
    return true;
  }

  // The library this page draws from, its own made components included.
  library(): Record<string, Entry> {
    return library(this.definitions());
  }

  entry(type: string): Entry | undefined {
    return this.library()[type];
  }

  // Who a peer is, for history: written once, by the peer itself.
  name(peer: string, name: string) {
    if (this.people.get(peer) === name) return;
    this.people.set(peer, name);
    this.doc.commit({ origin: "aside:people" });
  }

  // Enter in the middle of a text: the rest becomes a new paragraph after
  // this node, with its marks.
  split(id: TreeID, at: number, origin = "edit"): TreeID | undefined {
    const text = this.text(id);
    if (!text) return undefined;
    const tail = text.sliceDelta(at, text.length);
    if (text.length > at) text.delete(at, text.length - at);
    const parent = this.parent(id);
    const next = parent === undefined ? this.tree.createNode(undefined, this.index(id) + 1) : this.node(parent)!.createNode(this.index(id) + 1);
    // An item or a row's cell splits into another of its kind.
    const kind = this.type(id);
    this.build(next, { type: kind === "item" ? "item" : "paragraph" });
    (next.data.get("text") as LoroText).applyDelta(tail);
    this.doc.commit({ origin });
    return next.id;
  }

  // Backspace at the start of a text: it joins the text before it, if the
  // two are siblings. Returns where the join is, for the cursor.
  join(id: TreeID, origin = "edit"): { into: TreeID; at: number } | undefined {
    const text = this.text(id);
    const siblings = this.children(this.parent(id));
    const before = siblings[siblings.indexOf(id) - 1];
    const into = before === undefined ? undefined : this.text(before);
    if (!text || !into || before === undefined) return undefined;
    const at = into.length;
    into.applyDelta([{ retain: at }, ...text.toDelta()]);
    this.tree.delete(id);
    this.doc.commit({ origin });
    return { into: before, at };
  }

  // Cut is a move into this tab's clipboard, and paste a move out of it, so
  // the block keeps its id, its styles and whatever anyone types in it.
  // meets: move/cut-paste
  cut(id: TreeID, origin = "edit"): boolean {
    if (!this.node(id) || HIDDEN.has(this.type(id) ?? "")) return false;
    let board = this.board();
    if (board === undefined) {
      const n = this.tree.createNode();
      this.build(n, { type: CLIPBOARD, props: { peer: this.doc.peerIdStr } });
      board = n.id;
    }
    this.tree.move(id, board, 0);
    this.doc.commit({ origin });
    return true;
  }

  // The block most recently cut in this tab, if any.
  get clipboard(): TreeID | undefined {
    const board = this.board();
    return board === undefined ? undefined : this.children(board)[0];
  }

  paste(parent: TreeID | undefined, index: number, origin = "edit"): TreeID | undefined {
    const id = this.clipboard;
    if (id === undefined || (parent !== undefined && !this.accepts(parent))) return undefined;
    const at = Math.min(index, this.children(parent).filter((c) => this.type(c) !== CLIPBOARD).length);
    this.tree.move(id, parent, at);
    this.doc.commit({ origin });
    return id;
  }

  private board(): TreeID | undefined {
    const me = this.doc.peerIdStr;
    return this.tree.roots().find((n) => n.data.get("type") === CLIPBOARD && (n.data.get("props") as LoroMap | undefined)?.get("peer") === me)?.id;
  }

  // Make a component from sibling blocks: they move into its definition,
  // and one instance of it takes their place. `expose` names the fields an
  // instance may change. Returns the instance, or why it can't be made.
  // meets: compose/new
  compose(ids: TreeID[], name: string, expose: Expose, origin = "edit"): { id: TreeID } | { error: string } {
    if (!NAME.test(name)) return { error: "A component's name starts with a capital letter, then letters and digits." };
    if (this.entry(name)) return { error: `There is already a component called “${name}”.` };
    if (!ids.length || ids.some((id) => !this.node(id))) return { error: "Select the blocks to make it from." };
    const parent = this.parent(ids[0]);
    if (ids.some((id) => this.parent(id) !== parent)) return { error: "The blocks must be side by side, in one container." };
    const order = [...ids].sort((a, b) => this.index(a) - this.index(b));
    for (const [k, e] of Object.entries(expose)) {
      if (!/^[a-z][a-zA-Z0-9]{0,30}$/.test(k)) return { error: `“${k}” can't be a setting's name.` };
      if (!order.some((id) => id === e.node || this.within(e.node, id))) return { error: `“${k}” isn't in the selected blocks.` };
    }
    const at = this.index(order[0]);
    const def = this.tree.createNode();
    this.build(def, { type: DEFINITION, props: { name, expose } });
    order.forEach((id, i) => this.tree.move(id, def.id, i));
    const node = parent === undefined ? this.tree.createNode(undefined, at) : this.node(parent)!.createNode(at);
    this.build(node, { type: name });
    this.doc.commit({ origin });
    return { id: node.id };
  }

  private within(id: TreeID, ancestor: TreeID): boolean {
    for (let p = this.parent(id); p !== undefined; p = this.parent(p)) if (p === ancestor) return true;
    return false;
  }

  // The page: every root but the hidden ones.
  toJSON(): NodeJSON[] {
    return this.roots().filter((n) => !HIDDEN.has(n.type));
  }

  // Components made on this page.
  definitions(): NodeJSON[] {
    return this.roots().filter((n) => n.type === DEFINITION);
  }

  // What the text form holds: the page and its components, not clipboards.
  roots(): NodeJSON[] {
    return this.tree.roots().filter((n) => n.data.get("type") !== CLIPBOARD).map(json);
  }

  // Make the page match a target (the source view's text, a version, a
  // file) with the edits a person would make: nodes that kept their id
  // move and change in place, the rest are inserted or deleted, and text
  // changes by its smallest difference. Clipboards are left alone.
  match(target: Target, origin = "edit") {
    const seen = new Set<TreeID>();
    const place = (parent: TreeID | undefined, specs: Spec[]) =>
      specs.forEach((s, i) => {
        let id = s.id as TreeID | undefined;
        if (id === undefined || seen.has(id) || !this.node(id) || this.inClipboard(id)) {
          const node = parent === undefined ? this.tree.createNode(undefined, i) : this.node(parent)!.createNode(i);
          this.build(node, { ...s, id: "new", children: [] });
          if (s.id !== undefined) this.renamed!.set(s.id, node.id);
          id = node.id;
        } else {
          if (this.parent(id) !== parent || this.index(id) !== i) this.tree.move(id, parent, i);
          this.update(id, s);
        }
        seen.add(id);
        place(id, s.children ?? []);
      });
    this.renamed = new Map();
    place(undefined, target.nodes);
    this.relink();
    const sweep = (ids: TreeID[]) =>
      ids.forEach((id) => {
        if (this.type(id) === CLIPBOARD) return;
        if (!seen.has(id)) this.tree.delete(id);
        else sweep(this.children(id));
      });
    sweep(this.children());
    const tokens = this.tokens.toJSON() as Record<string, string>;
    for (const k of Object.keys(tokens)) if (!(k in target.tokens)) this.tokens.delete(k);
    for (const [k, v] of Object.entries(target.tokens)) if (tokens[k] !== v && allowToken(v)) this.tokens.set(k, v);
    this.doc.commit({ origin });
  }

  private inClipboard(id: TreeID): boolean {
    for (let p: TreeID | undefined = id; p !== undefined; p = this.parent(p)) if (this.type(p) === CLIPBOARD) return true;
    return false;
  }

  private update(id: TreeID, s: Spec) {
    const d = this.data(id);
    if (d.get("type") !== s.type) d.set("type", s.type);
    const sync = (map: LoroMap, want: Record<string, unknown>, ok: (k: string, v: unknown) => boolean) => {
      const have = map.toJSON() as Record<string, unknown>;
      for (const k of Object.keys(have)) if (!(k in want)) map.delete(k);
      for (const [k, v] of Object.entries(want)) if (JSON.stringify(have[k]) !== JSON.stringify(v) && ok(k, v)) map.set(k, v as never);
    };
    sync(d.get("props") ?? d.setContainer("props", new LoroMap()), s.props ?? {}, () => true);
    sync(d.get("style") ?? d.setContainer("style", new LoroMap()), s.style ?? {}, (k, v) => allowStyle(k, v));
    if (s.md !== undefined && JSON.stringify(d.get("md")) !== JSON.stringify(s.md)) d.set("md", s.md);
    const want = s.delta ?? (s.text !== undefined ? (s.text ? [{ insert: s.text }] : []) : undefined);
    const text = d.get("text");
    if (want === undefined) {
      if (text instanceof LoroText) d.delete("text");
    } else setDelta(text instanceof LoroText ? text : d.setContainer("text", new LoroText()), want);
  }

  // Nodes made from specs that had ids (a file, an old version) get new
  // ids; a made component's exposed fields follow them.
  private renamed?: Map<string, TreeID>;
  private relink() {
    const renamed = this.renamed;
    this.renamed = undefined;
    if (!renamed?.size) return;
    for (const d of this.definitions()) {
      const expose = (d.props.expose ?? {}) as Expose;
      let changed = false;
      const next: Expose = {};
      for (const [k, e] of Object.entries(expose)) {
        const to = renamed.get(e.node);
        next[k] = to ? ((changed = true), { ...e, node: to }) : e;
      }
      if (changed) this.data(d.id).get("props").set("expose", next as never);
    }
  }

  private build(node: LoroTreeNode, s: Spec) {
    if (s.id !== undefined && s.id !== "new") this.renamed?.set(s.id, node.id);
    const d = node.data;
    d.set("type", s.type);
    const props = d.setContainer("props", new LoroMap());
    for (const [k, v] of Object.entries(s.props ?? {})) props.set(k, v as never);
    const style = d.setContainer("style", new LoroMap());
    for (const [k, v] of Object.entries(s.style ?? {})) if (allowStyle(k, v)) style.set(k, v);
    if (s.md) d.set("md", s.md);
    // A spec from the text form says whether it has text; any other gets
    // text if its component holds some.
    if (s.delta) setDelta(d.setContainer("text", new LoroText()), s.delta);
    else if (s.text !== undefined || (s.id === undefined && components[s.type]?.text)) {
      const t = d.setContainer("text", new LoroText());
      if (s.text) t.insert(0, s.text);
    }
    for (const c of s.children ?? []) this.build(node.createNode(), c);
  }
}

function json(n: LoroTreeNode): NodeJSON {
  const d = n.data as unknown as Data;
  const text = d.get("text");
  return {
    id: n.id,
    type: String(d.get("type") ?? ""),
    props: (d.get("props")?.toJSON() ?? {}) as Props,
    style: (d.get("style")?.toJSON() ?? {}) as Style,
    ...(text instanceof LoroText ? { text: text.toDelta() } : {}),
    ...(d.get("md") ? { md: d.get("md") as Md } : {}),
    children: (n.children() ?? []).map(json),
  };
}

export const allowToken = (v: string) => allowStyle("color", v) || allowStyle("padding", v);

// Make a text hold `want` by the smallest edit: the changed middle of its
// words, then whichever marks differ.
export function setDelta(text: LoroText, want: Delta<string>[]) {
  const have = text.toString();
  const next = want.map((o) => ("insert" in o ? o.insert ?? "" : "")).join("");
  let p = 0;
  while (p < have.length && p < next.length && have[p] === next[p]) p++;
  let q = 0;
  while (q < have.length - p && q < next.length - p && have[have.length - 1 - q] === next[next.length - 1 - q]) q++;
  if (have.length - p - q > 0) text.delete(p, have.length - p - q);
  if (next.length - p - q > 0) text.insert(p, next.slice(p, next.length - q));
  const spread = (d: Delta<string>[]) =>
    d.flatMap((o) => ("insert" in o && o.insert ? Array.from({ length: o.insert.length }, () => (o.attributes ?? {}) as Record<string, unknown>) : []));
  const now = spread(text.toDelta());
  const goal = spread(want);
  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  for (const key of Object.keys(MARKS)) {
    let i = 0;
    while (i < goal.length) {
      const v = goal[i][key];
      if (same(now[i]?.[key], v)) {
        i++;
        continue;
      }
      let j = i;
      while (j < goal.length && same(goal[j][key], v) && !same(now[j]?.[key], v)) j++;
      if (v == null) text.unmark({ start: i, end: j }, key);
      else text.mark({ start: i, end: j }, key, v as never);
      i = j;
    }
  }
}

function seedPeer(spec: Spec[]): bigint {
  // FNV-1a, 53 bits: any number will do as long as the same spec gives it.
  let h = 0xcbf29ce484222325n;
  for (const ch of JSON.stringify(spec)) h = ((h ^ BigInt(ch.codePointAt(0)!)) * 0x100000001b3n) & 0xffffffffffffffffn;
  return (h & 0x1fffffffffffffn) | 1n;
}

function randomPeer(): bigint {
  const [a, b] = crypto.getRandomValues(new Uint32Array(2));
  return (BigInt(a) << 21n) ^ BigInt(b) | 2n;
}
